import { earlierPhysics, type PhysicsTuning } from '../config/tuning';
import type { GranPremioApi } from '../net/api';
import { type InputFrame, packInput } from '../sim/input';
import { type SimEvent, type SimState, spawnState, stepWorld } from '../sim/world';
import type { GeneratedCircuit } from '../track/generate';
import {
  deltaAt,
  GHOST_STRIDE,
  type GhostTrack,
  type LapRecord,
  makeRecord,
  physicsHash,
  replayInputs,
  type ReplayJob,
  replayJob,
  replayRecord,
  sampleGhost,
} from './replay';
import { type DayRecord, loadDay, saveDay } from './storage';

export type SectorColor = 'purple' | 'green' | 'yellow' | 'invalid';
export type MedalName = 'bronze' | 'silver' | 'gold' | 'author';
export const MEDAL_ORDER: MedalName[] = ['bronze', 'silver', 'gold', 'author'];

export interface LapResult {
  lapIndex: number;
  ms: number;
  valid: boolean;
  sectorMs: number[];
  isPB: boolean;
  previousBestMs: number | null;
  /** Medals earned for the first time today on this lap. */
  medals: MedalName[];
  /**
   * Replaying the recorded inputs reproduced this lap exactly (PBs only). Checked over the frames after
   * the line: null until then, and a 'best' event (or 'bestRejected') follows.
   */
  verified: boolean | null;
  /** Laps driven with edited physics are never saved. */
  tuned: boolean;
  /** A practice lap (the driving lesson): timed, but never saved, never a PB, never on the board. */
  practice: boolean;
}

export type SessionEvent =
  | { type: 'restart' }
  | { type: 'lapStart'; lapIndex: number }
  | { type: 'sector'; sector: number; ms: number; color: SectorColor }
  | { type: 'lap'; result: LapResult }
  /** A new best's recording replayed exactly: it's now the day's best (saved, the ghost). */
  | { type: 'best'; result: LapResult }
  /** It didn't replay exactly (a bug): not kept. */
  | { type: 'bestRejected'; result: LapResult }
  | { type: 'invalidated' }
  | { type: 'barrier'; speed: number };

export type GhostSource = 'best' | 'bot' | 'none';

export class Session {
  readonly circuit: GeneratedCircuit;
  physics: PhysicsTuning;
  state: SimState;
  /** Pose at the previous tick, for render interpolation. */
  prevX = 0;
  prevY = 0;
  prevHeading = 0;
  prevS = 0;
  /** Packed inputs since the last restart. */
  packed: number[] = [];
  /** x, y, heading after every tick since the last restart (ghost self-check). */
  private livePoses: number[] = [];
  /**
   * A new best being checked, a slice per frame (verifyStep): its recording replayed and compared with where
   * the car actually was (this run's poses, kept with it: a restart starts a new list).
   */
  private checking: { record: LapRecord; result: LapResult; job: ReplayJob | null; live: number[] } | null = null;
  day: DayRecord;
  /** The ghost on screen (your best, the bot's lap, or none). */
  ghost: GhostTrack | null = null;
  ghostSource: GhostSource = 'best';
  /** Your best lap's trace: the live delta compares against this (or the rival's). */
  private pbTrack: GhostTrack | null = null;
  /** A lap you're racing against (a friend's link, a board lap): while set, it's the ghost and the gap. */
  private rival: GhostTrack | null = null;
  dayBestSectors: (number | null)[];
  lapSectors: (SectorColor | null)[] = [null, null, null];
  lapSectorMs: (number | null)[] = [null, null, null];
  lastLap: LapResult | null = null;
  lastVerification: { ok: boolean; detail: string } | null = null;
  /**
   * Practice (the driving lesson): laps are timed but never saved. Keep it on
   * until a restart, or a lap begun with the coach's help would be saved.
   */
  practice = false;
  private listeners: ((e: SessionEvent) => void)[] = [];
  private simEvents: SimEvent[] = [];

  /** Fingerprint of the day's car physics: records are only kept for exactly these. */
  readonly basePhysicsHash: string;

  constructor(circuit: GeneratedCircuit, private api: GranPremioApi, physics: PhysicsTuning = { ...circuit.physics }) {
    this.circuit = circuit;
    this.physics = physics;
    this.basePhysicsHash = physicsHash(circuit.physics);
    this.day = loadDay(circuit.date, circuit.trackHash, this.basePhysicsHash);
    // The day its car changed mid-race: what you drove earlier with the car the day began with still counts.
    if (this.day.laps === 0 && !this.day.best) {
      for (const p of earlierPhysics(circuit.era, circuit.date)) {
        const before = loadDay(circuit.date, circuit.trackHash, physicsHash(p));
        if (before.laps > 0 || before.best) {
          this.day = { ...before, physicsHash: this.basePhysicsHash };
          break;
        }
      }
    }
    this.dayBestSectors = this.day.bestSectors.slice();
    this.state = spawnState(circuit.runtime, physics);
    if (this.day.best) this.loadGhost(this.day.best);
    this.restart();
    void this.api.getDayBestSectors(circuit.date, circuit.trackHash, this.basePhysicsHash).then((s) => {
      for (let k = 0; k < 3; k++) {
        const v = s[k];
        if (v != null && (this.dayBestSectors[k] == null || v < (this.dayBestSectors[k] as number))) this.dayBestSectors[k] = v;
      }
    });
  }

  get tuned(): boolean {
    return physicsHash(this.physics) !== this.basePhysicsHash;
  }

  on(fn: (e: SessionEvent) => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((f) => f !== fn);
    };
  }

  private emit(e: SessionEvent): void {
    for (const fn of this.listeners) fn(e);
  }

  restart(): void {
    this.state = spawnState(this.circuit.runtime, this.physics);
    const c = this.state.car;
    this.prevX = c.x;
    this.prevY = c.y;
    this.prevHeading = c.heading;
    this.prevS = c.s;
    this.packed = [];
    this.livePoses = [];
    this.lapSectors = [null, null, null];
    this.lapSectorMs = [null, null, null];
    this.emit({ type: 'restart' });
  }

  step(input: InputFrame): void {
    const c = this.state.car;
    this.prevX = c.x;
    this.prevY = c.y;
    this.prevHeading = c.heading;
    this.prevS = c.s;
    this.packed.push(packInput(input));
    this.simEvents.length = 0;
    stepWorld(this.state, input, this.circuit.runtime, this.physics, this.simEvents);
    this.livePoses.push(c.x, c.y, c.heading);
    for (const e of this.simEvents) this.handle(e);
  }

  private sectorColor(k: number, ms: number): SectorColor {
    const dayBest = this.dayBestSectors[k];
    if (dayBest == null || ms < dayBest) return 'purple';
    const pb = this.day.best?.sectorMs[k];
    if (pb == null || ms < pb) return 'green';
    return 'yellow';
  }

  private handle(e: SimEvent): void {
    switch (e.type) {
      case 'lapStart':
        this.lapSectors = [null, null, null];
        this.lapSectorMs = [null, null, null];
        this.emit({ type: 'lapStart', lapIndex: e.lapIndex });
        break;
      case 'sector': {
        const color = this.state.timing.valid ? this.sectorColor(e.sector, e.ms) : 'invalid';
        this.lapSectors[e.sector] = color;
        this.lapSectorMs[e.sector] = e.ms;
        this.emit({ type: 'sector', sector: e.sector, ms: e.ms, color });
        break;
      }
      case 'invalidated':
        this.emit({ type: 'invalidated' });
        break;
      case 'barrier':
        this.emit({ type: 'barrier', speed: e.speed });
        break;
      case 'lapComplete':
        this.completeLap(e);
        break;
    }
  }

  private completeLap(e: Extract<SimEvent, { type: 'lapComplete' }>): void {
    const color3: SectorColor = e.valid ? this.sectorColor(2, e.sectorMs[2]) : 'invalid';
    this.lapSectors[2] = color3;
    this.lapSectorMs[2] = e.sectorMs[2];
    this.emit({ type: 'sector', sector: 2, ms: e.sectorMs[2], color: color3 });

    const tuned = this.tuned;
    const practice = this.practice;
    const noSave = tuned || practice;
    // The best so far: one still being checked is faster than the saved one.
    const prev = this.checking?.record.lapMs ?? this.day.best?.lapMs ?? null;
    const result: LapResult = {
      lapIndex: e.lapIndex,
      ms: e.ms,
      valid: e.valid,
      sectorMs: e.sectorMs,
      isPB: false,
      previousBestMs: prev,
      medals: [],
      verified: null,
      tuned,
      practice,
    };
    if (!noSave) {
      this.day.laps++;
      this.day.recent.unshift({ ms: e.ms, valid: e.valid, at: Date.now() });
      this.day.recent.length = Math.min(this.day.recent.length, 12);
    }
    if (e.valid && !noSave) {
      this.day.validLaps++;
      if (prev === null || e.ms < prev) {
        const record = makeRecord({
          date: this.circuit.date,
          trackHash: this.circuit.trackHash,
          physics: this.physics,
          lapMs: e.ms,
          sectorMs: e.sectorMs,
          lapIndex: e.lapIndex,
          endTick: e.endTick,
          packedInputs: this.packed,
        });
        // Self-check: the stored inputs must reproduce this exact lap. It's a new best now (the banner, the
        // cheer); it's kept once the check passes, a slice at a time over the next frames (verifyStep).
        result.isPB = true;
        this.checking = { record, result, job: replayJob(this.circuit.runtime, this.physics, record, true), live: this.livePoses };
      }
      for (let k = 0; k < 3; k++) {
        const v = e.sectorMs[k];
        if (this.day.bestSectors[k] == null || v < (this.day.bestSectors[k] as number)) this.day.bestSectors[k] = v;
        if (this.dayBestSectors[k] == null || v < (this.dayBestSectors[k] as number)) this.dayBestSectors[k] = v;
      }
      for (const m of MEDAL_ORDER) {
        if (!this.day.medals[m] && e.ms <= this.circuit.medals[m]) {
          this.day.medals[m] = true;
          result.medals.push(m);
        }
      }
    }
    if (!noSave) saveDay(this.day);
    this.lastLap = result;
    this.emit({ type: 'lap', result });
  }

  /** Every ghost frame must sit exactly where the car actually was. */
  private ghostMatchesLive(g: GhostTrack | null, live: number[]): boolean {
    if (!g) return false;
    for (let f = 0; f < g.count; f++) {
      const k = (g.firstTick + f - 1) * 3;
      if (k < 0) continue;
      if (g.frames[f * GHOST_STRIDE] !== live[k]) return false;
      if (g.frames[f * GHOST_STRIDE + 1] !== live[k + 1]) return false;
      if (g.frames[f * GHOST_STRIDE + 2] !== live[k + 2]) return false;
    }
    return true;
  }

  /** A new best is still being checked. */
  get checkingBest(): boolean {
    return this.checking !== null;
  }

  /**
   * Check a new best for up to `budgetMs` this frame (the game gives it a few milliseconds a frame, so a long
   * run's replay never freezes a lap); Infinity finishes it now (pausing, leaving the page, tests).
   */
  verifyStep(budgetMs: number): void {
    const c = this.checking;
    if (!c) return;
    const t0 = performance.now();
    let done: ReturnType<ReplayJob['step']> = null;
    if (c.job) {
      do done = c.job.step(budgetMs === Infinity ? Infinity : 240);
      while (!done && performance.now() - t0 < budgetMs);
      if (!done) return;
    }
    this.checking = null;
    const { record, result } = c;
    const ok = !!done && done.completed && done.lapMs === record.lapMs && done.endTick === record.endTick && this.ghostMatchesLive(done.ghost, c.live);
    result.verified = ok;
    this.lastVerification = {
      ok,
      detail: ok ? `lap ${record.lapMs} ms replayed exactly (${done?.ghost?.count ?? 0} frames)` : `replay gave ${done?.lapMs ?? 0} ms / tick ${done?.endTick ?? 0}`,
    };
    if (!ok) {
      result.isPB = false;
      this.emit({ type: 'bestRejected', result });
      return;
    }
    this.day.best = record;
    this.pbTrack = done?.ghost ?? null;
    if (this.ghostSource === 'best') this.ghost = this.pbTrack;
    saveDay(this.day);
    this.emit({ type: 'best', result });
  }

  /**
   * The car a lap was driven with: the day's, or on a day whose car changed mid-race the one it began with.
   * Null: not a car of this day (it can't be replayed here).
   */
  physicsOf(record: LapRecord): PhysicsTuning | null {
    return this.physicsByHash(record.physicsHash);
  }

  /** The day's car with this fingerprint (or the one the day began with), or null. */
  physicsByHash(hash: string): PhysicsTuning | null {
    if (hash === this.basePhysicsHash) return this.circuit.physics;
    for (const p of earlierPhysics(this.circuit.era, this.circuit.date)) if (physicsHash(p) === hash) return p;
    return null;
  }

  /**
   * Your lap from today's board when it's faster than any this device has (you raced it on another device, or
   * inside Discord): kept as this device's best, ghost, sectors and medals too, so what it shows and shares is
   * your real best. Only once it replays exactly here, and never while a new best is still being checked.
   */
  adoptBest(record: LapRecord): boolean {
    if (this.checking || record.date !== this.circuit.date || record.trackHash !== this.circuit.trackHash) return false;
    if (this.day.best && this.day.best.lapMs <= record.lapMs) return false;
    const ghost = this.replayed(record);
    if (!ghost) return false;
    this.day.best = record;
    this.pbTrack = ghost;
    if (this.ghostSource === 'best') this.ghost = ghost;
    for (let k = 0; k < 3; k++) {
      const v = record.sectorMs[k];
      if (v == null) continue;
      if (this.day.bestSectors[k] == null || v < (this.day.bestSectors[k] as number)) this.day.bestSectors[k] = v;
      if (this.dayBestSectors[k] == null || v < (this.dayBestSectors[k] as number)) this.dayBestSectors[k] = v;
    }
    for (const m of MEDAL_ORDER) if (record.lapMs <= this.circuit.medals[m]) this.day.medals[m] = true;
    saveDay(this.day);
    return true;
  }

  loadGhost(record: LapRecord): void {
    const ph = this.physicsOf(record);
    if (!ph) return;
    const r = replayRecord(this.circuit.runtime, ph, record, true);
    this.pbTrack = r.completed ? r.ghost : null;
    this.ghost = this.ghostSource === 'best' ? this.pbTrack : this.ghost;
  }

  /** Race against this lap (null: back to your own). False if it doesn't replay on this circuit and car. */
  setRival(record: LapRecord | null): boolean {
    this.rival = null;
    if (!record) return true;
    const ghost = this.replayed(record);
    this.rival = ghost;
    return ghost !== null;
  }

  /** Whether a lap replays exactly on this circuit and car (so it can be watched). */
  canReplay(record: LapRecord): boolean {
    return this.replayed(record) !== null;
  }

  private replayed(record: LapRecord): GhostTrack | null {
    const ph = this.physicsOf(record);
    if (!ph) return null;
    const r = replayRecord(this.circuit.runtime, ph, record, true);
    return r.completed && r.lapMs === record.lapMs && r.endTick === record.endTick ? r.ghost : null;
  }

  setGhostSource(src: GhostSource): void {
    this.ghostSource = src;
    this.ghost = src === 'best' ? this.pbTrack : null;
    if (src === 'bot') {
      const r = replayInputs(this.circuit.runtime, this.circuit.physics, this.circuit.bot.inputs, 1, true);
      this.ghost = r.completed ? r.ghost : null;
    }
  }

  // --- queries for the HUD and the renderer (renderTick = tick - 1 + alpha)

  lapElapsedMs(renderTick: number): number | null {
    const t = this.state.timing;
    if (!t.started) return null;
    return Math.max(0, ((renderTick - t.lapStart) * 1000) / 60);
  }

  liveDelta(renderTick: number): number | null {
    const t = this.state.timing;
    const ref = this.rival ?? this.pbTrack;
    if (!ref || !t.started || !t.valid) return null;
    const elapsed = this.lapElapsedMs(renderTick);
    if (elapsed === null || elapsed < 400) return null;
    return deltaAt(ref, this.state.car.s, elapsed);
  }

  ghostPose(renderTick: number, out: number[]): boolean {
    const t = this.state.timing;
    const g = this.rival ?? this.ghost;
    if (!g || !t.started) return false;
    return sampleGhost(g, g.lapStart + (renderTick - t.lapStart), out);
  }
}
